import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Button from '../button';
import Spinner from '../spinner';

/**
 * The authenticator code prompt.
 *
 * Shared because there are now two ways to arrive at a half-finished sign-in -- a password and an
 * emailed link -- and the second factor has to look and behave the same from both. It owns the
 * typing and nothing else: the caller holds the challenge token and decides what spending it
 * means.
 */
const TwoFactorPrompt = ({ recoveryCodes, error, busy, onSubmit }) => {
	const [code, setCode] = useState('');
	const [useRecovery, setUseRecovery] = useState(false);

	const spent = recoveryCodes === 0;

	const submit = (e) => {
		e.preventDefault();

		// The button is disabled in both cases, but Enter in the form would still submit.
		if( busy || !code.trim() ) {
			return;
		}

		// A recovery code is sent instead of a code rather than as a retry after one fails, so a
		// mistyped digit cannot quietly spend one of the ten.
		onSubmit(useRecovery ? { recoveryCode: code.trim() } : { code: code.trim() });
		setCode('');
	};

	return (
		<form name="twoFactorForm" onSubmit={submit}>
			<div className="px-auth-modal-header">
				<h3 className="px-auth-modal-header-title">Enter your code</h3>
			</div>

			<div className="px-auth-modal-body">
				{ error
					? (
						<div className="px-auth-modal-error" data-cy="two-factor-error">
							{ error }
						</div>
					)
					: null }

				<p className="px-auth-modal-hint">
					{ useRecovery
						? 'Enter one of the recovery codes you saved when you set this up.'
						: 'Open your authenticator app and enter the 6-digit code it shows.' }
				</p>

				<input
					id="twoFactorCode"
					// Kept out of LogRocket recordings; a recovery code typed here is good until
					// it is spent.
					data-private="true"
					type="text"
					inputMode={useRecovery ? 'text' : 'numeric'}
					autoComplete={useRecovery ? 'off' : 'one-time-code'}
					maxLength={useRecovery ? 11 : 6}
					placeholder={useRecovery ? 'Recovery code' : '000000'}
					// The hint above is a paragraph, not a label, and the placeholder changes with
					// the mode -- so the field names itself, for a screen reader in either mode.
					aria-label={useRecovery ? 'Recovery code' : 'Code from your authenticator app'}
					value={code}
					onChange={(e) => setCode(e.target.value)}
					data-cy="two-factor-code"
					autoFocus
				/>
			</div>

			<div className="px-auth-modal-footer">
				<p className="px-auth-modal-footer-forgot">
					{ !useRecovery && !spent
						? (
							<a
								href="#"
								data-cy="two-factor-use-recovery"
								onClick={(e) => {
									e.preventDefault();
									setUseRecovery(true);
									setCode('');
								}}
							>
								Lost your phone?
							</a>
						)
						: null }
					{ useRecovery
						? (
							<a
								href="#"
								data-cy="two-factor-use-code"
								onClick={(e) => {
									e.preventDefault();
									setUseRecovery(false);
									setCode('');
								}}
							>
								Use my authenticator instead
							</a>
						)
						: null }
				</p>

				{ !busy
					? (
						<Button
							type="submit"
							className="px-auth-modal-footer-btn"
							onClick={submit}
							disabled={!code.trim()}
							data-cy="two-factor-btn"
						>
							Continue
						</Button>
					)
					: (
						<Spinner small></Spinner>
					)}
			</div>
		</form>
	);
};

TwoFactorPrompt.propTypes = {
	// How many recovery codes are left, so somebody out of them is not sent to a form they
	// cannot fill.
	recoveryCodes: PropTypes.number,
	error: PropTypes.string,
	busy: PropTypes.bool,
	// Called with `{ code }` or `{ recoveryCode }` -- never both.
	onSubmit: PropTypes.func.isRequired
};

export default TwoFactorPrompt;
